import { useMemo } from 'react';
import { ptText } from '../lib/ptTime';
import { ticketLabel } from './SharedPicks';
import { legRank, blinkClass } from './ticketDate';

const PILL = {
  green: ['WON', 'bg-emerald-500/25 text-emerald-200 border-emerald-400'],
  red: ['LOST', 'bg-red-500/25 text-red-200 border-red-400'],
  blue: ['LEADING', 'bg-sky-500/25 text-sky-200 border-sky-400'],
  purple: ['50% THERE', 'bg-purple-500/25 text-purple-200 border-purple-400'],
  orange: ['TRAILING', 'bg-orange-500/25 text-orange-200 border-orange-500'],
  yellow: ['BELOW', 'bg-yellow-500/20 text-yellow-200 border-yellow-400'],
  neutral: ['NOT STARTED', 'bg-zinc-900 text-zinc-300 border-zinc-700'],
};
const status = (l) => {
  if (l.performance_status === 'LIVE_TIED') return ['TIED', PILL.yellow[1]];
  if (l.color_token === 'blue' && l.performance_status !== 'LIVE_LEADING') return ['75% THERE', PILL.blue[1]];
  return PILL[l.color_token] || PILL.neutral;
};
const pickedName = (l) => (l.picked_side === 'home' ? l.home_team : l.picked_side === 'away' ? l.away_team : null);
const keyOf = (l) => {
  const bt = (l.bet_type || 'moneyline').toLowerCase();
  const who = (bt === 'moneyline' || bt === 'spread') && pickedName(l) ? pickedName(l) : l.player_name || l.selection || '';
  return `${l.league || ''}|${bt}|${who.toLowerCase()}|${bt === 'moneyline' ? '' : l.line || l.selection || ''}`;
};
const score = (l) => {
  const me = l.picked_side === 'away' ? l.live_score_away : l.live_score_home;
  const them = l.picked_side === 'away' ? l.live_score_home : l.live_score_away;
  if (me == null || them == null) return null;
  return `${me}–${them}`;
};

// Picks that appear on 2+ open tickets, with how many and how each is doing right now.
export default function RepeatedPicks({ tickets }) {
  const rows = useMemo(() => {
    const by = {};
    tickets.forEach((t) => (t.legs || []).forEach((l) => {
      const k = keyOf(l);
      const g = (by[k] ||= { leg: l, ids: new Set(), labels: [] });
      if (!g.ids.has(t.id)) { g.ids.add(t.id); g.labels.push(ticketLabel(t)); }
    }));
    return Object.values(by).filter((g) => g.ids.size > 1).sort((a, b) => legRank(a.leg) - legRank(b.leg) || b.ids.size - a.ids.size);
  }, [tickets]);
  if (!rows.length) return null;
  const short = (l) => {
    const sel = (l.selection || '').replace(/\s+(ML|moneyline)$/i, '');
    return l.bet_type && l.bet_type !== 'moneyline' ? sel : sel.length && sel.length <= 16 ? sel : (pickedName(l) || sel).split(' ').slice(0, 2).join(' ');
  };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-2" data-testid="repeated-picks">
      <p className="text-xs font-bold text-white mb-1.5">On multiple tickets <span className="font-normal text-zinc-500">· {rows.length}</span></p>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-1">
        {rows.map(({ leg: l, ids, labels }) => {
          const [txt, cls] = status(l);
          const sc = score(l);
          const live = ['blue', 'purple', 'yellow'].includes(l.color_token);
          const sub = l.color_token === 'neutral' ? ptText(l.live_period).replace(/^\d+\/\d+\s*-\s*/, '') : `${sc || ''}${live && l.live_period ? ` · ${l.live_period.replace(/ - /, ' ')}` : ''}`;
          return (
            <div key={keyOf(l)} className={`rounded-md border px-1.5 py-1 min-w-0 ${cls}${blinkClass(l.blink, l.color_token)}`} title={`${txt}\n${labels.join('\n')}`}
              data-testid={`repeated-pick-${keyOf(l).replace(/[^a-z0-9]+/gi, '-')}`}>
              <div className="flex items-center gap-1 min-w-0">
                {l.team_logo && <img src={l.team_logo} alt="" className="w-4 h-4 rounded-full bg-white/90 object-contain shrink-0" />}
                <span className="text-[11px] font-bold truncate flex-1">{short(l)}</span>
                <span className="text-[10px] font-black shrink-0">×{ids.size}</span>
              </div>
              <p className="text-[9px] opacity-80 truncate">{sub || txt}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
